import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { grantsApi } from "../lib/api.js";
import { STATUS_META, scoreBand, formatCurrency, formatDate } from "../lib/constants.js";

export default function GrantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await grantsApi.get(id);
        if (active) setData(res.data);
      } catch (err) {
        if (active) setError(err.message);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [id]);

  if (loading) return <div className="card p-10 text-center text-sm text-ink-500">Loading…</div>;
  if (error) return <div role="alert" className="card p-6 text-sm text-red-700">{error}</div>;
  if (!data) return null;

  const { grant, evaluations } = data;
  const status = STATUS_META[grant.status] || STATUS_META.pending;

  return (
    <div className="space-y-6">
      <Link to="/" className="text-sm text-brand-700 hover:underline">← Back to Dashboard</Link>

      <div className="card p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-ink-900">{grant.projectTitle}</h1>
            <p className="text-sm text-ink-500">{grant.organization} · {grant.grantId}</p>
          </div>
          <span className={`badge ${status.classes}`}>{status.label}</span>
        </div>
        <dl className="mt-4 grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
          <div>
            <dt className="text-ink-500">Requested Budget</dt>
            <dd className="font-semibold text-ink-900">{formatCurrency(grant.requestedBudget)}</dd>
          </div>
          <div>
            <dt className="text-ink-500">Funding Cycle</dt>
            <dd className="font-semibold text-ink-900">{grant.fundingCycle || "—"}</dd>
          </div>
          <div>
            <dt className="text-ink-500">Submitted</dt>
            <dd className="font-semibold text-ink-900">{formatDate(grant.createdAt)}</dd>
          </div>
          <div>
            <dt className="text-ink-500">Evaluations</dt>
            <dd className="font-semibold text-ink-900">{evaluations.length}</dd>
          </div>
        </dl>
        <div className="mt-4">
          <h2 className="mb-1 text-sm font-medium text-ink-700">Abstract Summary</h2>
          <p className="text-sm text-ink-600">{grant.abstractSummary}</p>
        </div>
        <div className="mt-4 flex gap-2">
          <a href={grant.documentUrl} target="_blank" rel="noopener noreferrer" className="btn-secondary">
            Open External Document (PDF)
          </a>
          <button className="btn-primary" onClick={() => navigate(`/evaluate?grant=${grant._id}`)}>
            Evaluate This Grant
          </button>
        </div>
      </div>

      <div className="card p-6">
        <h2 className="mb-3 text-lg font-semibold text-ink-900">Submitted Evaluations</h2>
        {evaluations.length === 0 ? (
          <p className="text-sm text-ink-500">No evaluations submitted yet.</p>
        ) : (
          <ul className="space-y-4">
            {evaluations.map((e) => {
              const band = scoreBand(e.weightedAverage);
              return (
                <li key={e._id} className="rounded-lg border border-ink-200 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-medium text-ink-800">
                        {e.evaluator?.name || "Evaluator"} · <span className="text-ink-500">{e.evaluator?.role}</span>
                      </p>
                      <p className="text-xs text-ink-400">Submitted {formatDate(e.audit?.lastModified)}</p>
                    </div>
                    <div className={`text-right`}>
                      <p className={`text-2xl font-bold ${band.classes}`}>{e.weightedAverage}</p>
                      <p className="text-xs text-ink-400">{band.label}</p>
                    </div>
                  </div>
                  <ul className="mt-3 grid grid-cols-1 gap-1 text-sm sm:grid-cols-3">
                    {e.scores.map((s, i) => (
                      <li key={i} className="rounded bg-ink-50 px-3 py-2">
                        <span className="text-ink-500">{s.criterion}:</span>{" "}
                        <span className="font-semibold text-ink-800">{s.score}</span>
                        <span className="text-ink-400"> (w{s.weight})</span>
                      </li>
                    ))}
                  </ul>
                  {e.feedback && <p className="mt-3 text-sm text-ink-600">“{e.feedback}”</p>}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
